// Модель конструктора AB-тестов: чистые функции без React.
// Аудитории — общие с кампаниями (campaignFormModel), здесь — специфика AB: id, когорты, payload.
import dayjs from "dayjs";
import {v4 as uuid} from "uuid";
import {MODE} from "../Campaign/campaignMode";
import {createAudience, parametersFromApiAudience} from "../Campaign/campaignFormModel";
import {prepareCohortsForSave, prepareParametersForSave} from "../CommonSummary";
import {convertToDayjs, isEmpty, isValidInteger, isValidJson} from "../Utils/CommonUtils";

export const STEPS = {
    BASIC: 'basic',
    AUDIENCE: 'audience',
    COHORTS: 'cohorts',
    SUMMARY: 'summary',
};
export const STEP_ORDER = [STEPS.BASIC, STEPS.AUDIENCE, STEPS.COHORTS, STEPS.SUMMARY];
export const ID_CHECK_DELAY = 1000;
export const COHORT_PREFIX = 'Cohort';
export const LINK_REGEXP = /^https:\/\/(?:app\.)?asana\.com\/.*$/;
const DEFAULT_COHORT_ID = /^Cohort \d+$/;

export const abTestIdRegexp = (domainAlias) => (domainAlias === 'cm' || domainAlias === 'staging')
    ? /^\d{8}_AB_[A-Za-z0-9_]+$/
    : /^(\d{4}_\d{2}_\d{2}_AB_[A-Za-z0-9_]+|Config_[A-Za-z0-9_]+)$/;

// ---------------------------------------------------------------------------
// Новые объекты
// ---------------------------------------------------------------------------

export class Cohort {
    constructor(uuid, id = 'Cohort 1', active = true, limited = false, limit = '-1', weight = '1', count = 1, parameters = '{}', size = -1) {
        this.id = id;
        this.active = active;
        this.limited = limited;
        this.limit = limit;
        this.weight = weight;
        this.count = count;
        this.parameters = parameters;
        this.size = size;
        this.uuid = uuid;
        this.creationDate = Math.round(Date.now() / 1000);
    }
}

export const createCohort = (number) => new Cohort(uuid(), `${COHORT_PREFIX} ${number}`, true, false, '-1', '1', number);

/**
 * Новый AB-тест. Раньше from/till вычислялись один раз при загрузке модуля (и с миллисекундами),
 * поэтому в форме, открытой позже, даты были в прошлом, а в payload уходили дробные timestamp.
 */
export const createAbTest = ({domainAlias} = {}) => {
    const from = dayjs().startOf('minute');
    return {
        abTestId: "",
        from,
        till: from.add(1, 'minute'),
        distributionType: "fixed",
        duration: 0,
        groupId: "",
        fickle: false,
        priority: 0,
        reassignCohort: false,
        assignCohortByPayments: domainAlias === 'cm',
        metaData: {link: ""},
        audiences: [createAudience(1)],
        cohorts: [new Cohort(uuid())],
    };
};

// ---------------------------------------------------------------------------
// Шаг "basic"
// ---------------------------------------------------------------------------

/** Изменение поля шага basic (event.target name/value/checked). */
export const applyField = (abTest, name, value, checked) => {
    if (name === 'priority') {
        const priority = +value;
        return {...abTest, priority: (priority === 0 || priority > 0) ? priority : abTest.priority};
    }
    if (name === 'reassignCohort' || name === 'assignCohortByPayments') return {...abTest, [name]: checked};
    // раньше metaData заменялась на {link}, и остальные поля metaData терялись при сохранении
    if (name === 'link') return {...abTest, metaData: {...abTest.metaData, link: value}};
    const next = {...abTest, [name]: value};
    if (name === 'abTestId' || name === 'groupId') next.groupId = value;
    return next;
};

export const applyDate = (abTest, field, date) => ({...abTest, [field]: dayjs(new Date(date.set('second', 0).valueOf()))});

export const isLinkInvalid = (link) => !!link && !LINK_REGEXP.test(link);

/** Ошибки шага basic: abTestIdEmpty, regexp, clone, link, date. */
export const validateBasic = (abTest, {mode, domainAlias}) => {
    const errors = new Set();
    const id = abTest.abTestId || '';
    if (isEmpty(id)) errors.add('abTestIdEmpty');
    else if (mode !== MODE.UPDATE && !abTestIdRegexp(domainAlias).test(id)) errors.add('regexp');
    if (id.toLowerCase().includes('clone')) errors.add('clone');
    if (isLinkInvalid(abTest.metaData?.link)) errors.add('link');
    if (dayjs.isDayjs(abTest.from) && dayjs.isDayjs(abTest.till) && !abTest.from.isBefore(abTest.till)) errors.add('date');
    return errors;
};

// ---------------------------------------------------------------------------
// Шаг "cohorts"
// ---------------------------------------------------------------------------

export const isCohortLimited = (cohort) => cohort.hasOwnProperty('limited') ? !!cohort.limited : cohort.limit != '-1'; // eslint-disable-line eqeqeq

const nextCohortNumber = (cohorts) => {
    const numbers = cohorts.map(c => c.count).filter(n => typeof n === 'number').sort((a, b) => a - b);
    const max = numbers[numbers.length - 1];
    for (let i = 1; i < max; i++) {
        if (!numbers.includes(i)) return i;
    }
    return cohorts.length + 1;
};

export const addCohort = (abTest) => ({...abTest, cohorts: [...abTest.cohorts, createCohort(nextCohortNumber(abTest.cohorts))]});

export const deleteCohort = (abTest, index) => abTest.cohorts.length > 1
    ? {...abTest, cohorts: abTest.cohorts.filter((_, i) => i !== index)}
    : abTest;

export const updateCohort = (abTest, index, field, value) => ({
    ...abTest,
    cohorts: abTest.cohorts.map((cohort, i) => {
        if (i !== index) return cohort;
        const next = {...cohort, [field]: value};
        if (field === 'limited' && value === false) next.limit = -1;
        return next;
    }),
});

/**
 * Ошибки когорт: Set ключей (idError, limitError, weightError, parametersError, userGroupError) по индексу когорты.
 * Раньше ошибки регистрировались компонентом только для открытой когорты и привязывались к индексу,
 * поэтому после удаления когорты могли "залипать".
 */
export const validateCohorts = (cohorts) => {
    const errors = cohorts.map(() => new Set());
    const idCounts = cohorts.reduce((acc, c) => ({...acc, [c.id]: (acc[c.id] || 0) + 1}), {});
    cohorts.forEach((cohort, index) => {
        const cohortErrors = errors[index];
        // имя по умолчанию "Cohort N" нужно заменить; одинаковые id перезаписали бы друг друга при сохранении
        if (isEmpty(cohort.id) || DEFAULT_COHORT_ID.test(cohort.id) || idCounts[cohort.id] > 1) cohortErrors.add('idError');
        if (!isValidInteger(cohort.limit) || (isCohortLimited(cohort) && cohort.limit < 0)) cohortErrors.add('limitError');
        if (!isValidInteger(cohort.weight) || cohort.weight < 0) cohortErrors.add('weightError');
        if (!isValidJson(cohort.parameters)) cohortErrors.add('parametersError');
        const groups = cohort.userGroup || [];
        const shared = cohorts.some((other, otherIndex) => otherIndex !== index
            && (other.userGroup || []).some(group => groups.includes(group)));
        if (shared) cohortErrors.add('userGroupError');
    });
    const errorCohortIndexes = errors.map((set, index) => set.size > 0 ? index : -1).filter(index => index >= 0);
    return {errors, errorCohortIndexes, hasErrors: errorCohortIndexes.length > 0};
};

// ---------------------------------------------------------------------------
// Кнопка NEXT
// ---------------------------------------------------------------------------

/** Почему нельзя перейти дальше (или null). */
export const getNextBlockReason = ({step, mode, basicErrors, idCheck, audienceHasErrors, cohortsHaveErrors}) => {
    if (step === STEPS.BASIC) {
        if (basicErrors.has('abTestIdEmpty')) return 'Fill in AB Test ID';
        if (basicErrors.has('regexp')) return 'AB Test ID must match the naming rules';
        if (basicErrors.has('clone')) return 'Remove "Clone" from AB Test ID';
        if (basicErrors.has('link')) return 'Fix the link';
        if (basicErrors.has('date')) return 'Till must be after From';
        if (mode !== MODE.UPDATE) {
            if (idCheck.pending) return 'Checking AB Test ID…';
            if (!idCheck.isFree) return 'This AB Test ID is already taken';
        }
    }
    if (step === STEPS.AUDIENCE && audienceHasErrors) return 'Fix audience errors';
    if (step === STEPS.COHORTS && cohortsHaveErrors) return 'Fix cohort errors';
    return null;
};

// ---------------------------------------------------------------------------
// Сохранение / загрузка
// ---------------------------------------------------------------------------

const toUnix = (date) => Math.floor(date.valueOf() / 1000);

export const buildSavePayload = (abTest) => {
    const audiences = abTest.audiences
        .map(audience => audience.parameters.reduce((acc, parameterSet) => prepareParametersForSave(acc, parameterSet), {}))
        .filter(audience => Object.keys(audience).length > 0);
    return {
        id: abTest.abTestId,
        groupId: abTest.groupId,
        priority: abTest.priority,
        assignCohortByPayments: abTest.assignCohortByPayments,
        reassignCohort: abTest.reassignCohort,
        reassignCohortDate: abTest.reassignCohortDate,
        fickle: abTest.fickle,
        distribution: {
            type: 'fixed',
            durationHour: 0.0,
            from: toUnix(abTest.from),
            till: toUnix(abTest.till),
        },
        audiences,
        cohorts: prepareCohortsForSave(abTest.cohorts),
        metaData: abTest.metaData,
    };
};

const isEmptyApiAudience = (audience) => Object.values(audience).every(value => !Array.isArray(value) || value.length === 0);

/**
 * Параметры аудиторий из API. Раньше offerInactiveRule и offerTypeInactiveRule загружались дважды
 * (вторая копия — с пустым значением), а offerTagInactiveRule не загружался вовсе и терялся при сохранении.
 */
export const audiencesFromApi = (apiAudiences) => (apiAudiences || []).map((audience, index) => ({
    name: `Audience ${index + 1}`,
    count: index + 1,
    // у AB-тестов пустая аудитория остаётся пустой (без пустой строки условия)
    parameters: isEmptyApiAudience(audience) ? [] : parametersFromApiAudience(audience),
}));

const toUserGroups = (users) => {
    if (!users) return [];
    return typeof users === 'string' ? [users] : users;
};

export const cohortsFromApi = (apiCohorts) => Object.values(apiCohorts || {}).map(cohort => ({
    id: cohort.id,
    limit: cohort.limit,
    weight: cohort.weight,
    active: cohort.active,
    parameters: JSON.stringify(cohort.parameters),
    size: cohort.size,
    userGroup: toUserGroups(cohort.userGroups),
    creationDate: cohort.creationDate,
    // раньше у загруженных когорт не было uuid, и их ошибки смешивались
    uuid: uuid(),
}));

export const abTestFromApi = (source, {mode}) => ({
    abTestId: mode === MODE.CLONE ? source.id + ' (Clone)' : source.id,
    groupId: source.groupId,
    fickle: source.fickle,
    priority: source.priority,
    assignCohortByPayments: source.assignCohortByPayments,
    reassignCohort: false,
    reassignCohortDate: source.reassignCohortDate,
    from: convertToDayjs(source.distribution.from),
    till: convertToDayjs(source.distribution.till),
    audiences: audiencesFromApi(source.audiences),
    cohorts: cohortsFromApi(source.cohorts),
    metaData: source.metaData,
});

/**
 * Ввод в диапазон NUMBER_RANGE нормализуется как раньше ("007" -> "7"),
 * но промежуточный ввод вроде "-" больше не превращается в "NaN".
 */
export const normalizeRangeInput = (parameterSet, field, value) => {
    if (field === 'fromIncl' || field === 'toIncl') return value;
    if (!parameterSet?.parameterData?.allowedRules?.includes('NUMBER_RANGE')) return value;
    if (value === '' || value.toUpperCase() !== value.toLowerCase()) return value + '';
    const number = +value;
    return Number.isNaN(number) ? value : String(number);
};
